import React, { ReactNode } from 'react';
import { formatEnumLabel } from '../helper';
import { scenarioTestingApi } from '../../apis';
import {
  InternalApiScenarioComputedParameter,
  InternalApiScenarioIntervalTimeUnit,
  InternalApiScenarioTestResultStatus,
  InternalApiScenarioVersionParameter,
  InternalApiScenarioVersionParameterTimeUnit,
  InternalApiScenarioVersionParameterType,
  InternalApiScenarioVersionStatus,
} from '../../api-internal';
import { DEFAULT_TEST_RUNS_PAGE_SIZE } from '../../scenario/versions/scenario-testing/TestRuns';
import { getValidParameters } from '../../scenario/versions/version-modal/ParametersEditor';
import { Ace } from 'ace-builds';

export const SCENARIO_PARAMETER_REGEX = /^\$[a-zA-Z][\w.]*$/;
export const SCENARIO_PARAMETER_REGEX_GLOBAL = /\$[a-zA-Z][\w.]*/g;
export const SCENARIO_PARAMETER_REGEX_END_OF_LINE = /\$[\w.]*$/;

export const RESERVED_SCENARIO_PARAMETER_NAMES = ['param'];

export const suppressionIntervalToDays = (suppressionInterval: number) => {
  const SECONDS_IN_DAY = 86400;
  return suppressionInterval ? Math.floor(suppressionInterval / SECONDS_IN_DAY) : 0;
};

export const formatApiScenarioContentStatusEnum = (status: InternalApiScenarioVersionStatus) => {
  switch (status) {
    case InternalApiScenarioVersionStatus.LIVE:
      return 'Current';
    case InternalApiScenarioVersionStatus.DRAFT:
    case InternalApiScenarioVersionStatus.HISTORIC:
    default:
      return formatEnumLabel(status.toString());
  }
};

export const scenarioVersionStatusColorVariant = (status: InternalApiScenarioVersionStatus) => {
  switch (status) {
    case InternalApiScenarioVersionStatus.LIVE:
      return 'success';
    case InternalApiScenarioVersionStatus.DRAFT:
      return 'primary';
    case InternalApiScenarioVersionStatus.HISTORIC:
      return 'secondary';
  }
};

export const getScenarioVersionStatus = (status: InternalApiScenarioVersionStatus, inReview?: boolean) => {
  if (status === InternalApiScenarioVersionStatus.DRAFT && inReview) {
    return 'Pending';
  } else if (status === InternalApiScenarioVersionStatus.LIVE) {
    return 'Current';
  } else if (status === InternalApiScenarioVersionStatus.DRAFT) {
    return 'Draft';
  } else if (status === InternalApiScenarioVersionStatus.HISTORIC) {
    return 'Historic';
  }
  throw Error('Unreachable');
};

export const testRunStatusColorVariant = (status: InternalApiScenarioTestResultStatus) => {
  switch (status) {
    case InternalApiScenarioTestResultStatus.FINISHED:
      return 'success';
    case InternalApiScenarioTestResultStatus.RUNNING:
      return 'primary';
    case InternalApiScenarioTestResultStatus.FAILED:
      return 'danger';
    case InternalApiScenarioTestResultStatus.SCHEDULED:
      return 'warning';
    case InternalApiScenarioTestResultStatus.CANCELLED:
      return 'secondary';
  }
};

export interface TimeUnits {
  hours: number;
  minutes: number;
  seconds: number;
}

export const millisToTimeUnits = (millis: number): TimeUnits => {
  return secondsToTimeUnits(millis / 1000);
};

const secondsToTimeUnits = (seconds: number): TimeUnits => {
  return {
    hours: Math.floor(seconds / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: Math.floor(seconds % 60),
  };
};

export const fetchTestRuns = async (scenarioHandle: string, page: number) => {
  return await scenarioTestingApi.getScenarioTestRuns({
    scenarioHandle,
    page,
    size: DEFAULT_TEST_RUNS_PAGE_SIZE,
  });
};

export const humanizePeriod = (
  value: number,
  timeUnit: InternalApiScenarioVersionParameterTimeUnit | InternalApiScenarioIntervalTimeUnit
) => {
  switch (timeUnit) {
    case InternalApiScenarioVersionParameterTimeUnit.HOUR:
      return value + ' ' + (value === 1 ? 'hour' : 'hours');
    case InternalApiScenarioVersionParameterTimeUnit.DAY:
      return value + ' ' + (value === 1 ? 'day' : 'days');
    case InternalApiScenarioVersionParameterTimeUnit.WEEK:
      return value + ' ' + (value === 1 ? 'week' : 'weeks');
    case InternalApiScenarioVersionParameterTimeUnit.MONTH:
      return value + ' ' + (value === 1 ? 'month' : 'months');
  }
};

export const assignParameterValues = (input: string, parameters: InternalApiScenarioVersionParameter[]): ReactNode => {
  const parametersSplitRegex = [...parameters]
    // should be sorted to match the longer names first (in case there is same root)
    .sort((a, b) => b.name.length - a.name.length)
    .map((p) => `(\\$${p.name})`)
    .join('|');

  const chunks: string[] = parameters.length
    ? input.split(new RegExp(parametersSplitRegex, 'g')).filter(Boolean)
    : [input];

  const result = [];
  for (const chunk of chunks) {
    if (SCENARIO_PARAMETER_REGEX.test(chunk)) {
      const paramName = chunk.replace('$', '');
      const param = parameters.find((param) => param.name === paramName);

      const value = param?.value;
      if (value === undefined) {
        result.push(<span className='text-primary'>{chunk}</span>);
      } else if (param?.type === InternalApiScenarioVersionParameterType.NUMERIC) {
        const paramValue = value.toLocaleString();
        result.push(<span className='text-primary'>{paramValue}</span>);
      } else if (param?.type === InternalApiScenarioVersionParameterType.PERIOD) {
        const period = param?.timeUnit && humanizePeriod(value as unknown as number, param.timeUnit);
        result.push(<span className='text-primary'>{period || value.toLocaleString()}</span>);
      } else {
        result.push(<span className='text-primary'>{(value as string[])?.join(', ') || ''}</span>);
      }
    } else {
      result.push(<span>{chunk}</span>);
    }
  }

  return (
    <>
      {result.map((r, i) => (
        <span key={i}>{r}</span>
      ))}
    </>
  );
};

export const getUserParametersCompleter = (parameters: InternalApiScenarioVersionParameter[]): Ace.Completer => {
  return {
    getCompletions: (_editor, _session, _pos, prefix, callback) => {
      const completionPrefix = prefix.toLowerCase().trim();

      const parameterSuggestions = getValidParameters(parameters)
        .map((p) => ({
          value: '$' + p.name,
          meta: p.displayName,
        }))
        .filter((p) => completionPrefix && p.value.startsWith(completionPrefix));

      callback(
        null,
        parameterSuggestions.map((item, index) => ({ ...item, caption: item.value, score: 1000 - index }))
      );
    },
  };
};

export const getQueryTemplateCompleter = (queryTemplateNames: string[]): Ace.Completer => {
  return {
    getCompletions: (_editor, _session, _pos, prefix, callback) => {
      const completionPrefix = prefix.toLowerCase().trim();

      const startsWithPrefix = queryTemplateNames
        .map((name) => '$' + name)
        .filter((name) => completionPrefix.includes(`${name}`.slice(0, 5))) // match query template only after first 4 letters
        .map((name) => ({ value: name, meta: 'Query Template' }));

      const containsPrefix = queryTemplateNames
        .map((name) => '$' + name)
        .filter(
          (name) =>
            completionPrefix.startsWith('$') &&
            completionPrefix.length > 4 &&
            !name.startsWith(completionPrefix) &&
            name.includes(completionPrefix.replace('$', ''))
        ) // match query template only after first 4 letters
        .map((name) => ({ value: name, meta: 'Query Template' }));

      const queryTemplateSuggestions = [...startsWithPrefix, ...containsPrefix].slice(0, 20);
      callback(
        null,
        queryTemplateSuggestions.map((item, index) => ({ ...item, caption: item.value, score: 1000 - index }))
      );
    },
  };
};

export const getComputedParametersCompleter = (variables: InternalApiScenarioComputedParameter[]): Ace.Completer => {
  const sorted = variables.sort((a, b) => a.name.localeCompare(b.name));
  return {
    getCompletions: (_editor, session, pos, _prefix, callback) => {
      const completionPrefix = session.doc
        .getLine(pos.row)
        .slice(0, pos.column)
        .match(SCENARIO_PARAMETER_REGEX_END_OF_LINE)
        ?.at(0);
      const dotCount = completionPrefix ? completionPrefix.split('.').length - 1 : 0;

      const fixedVariablesSuggestions = sorted
        .map((v) => ({
          value: '$' + v.name,
          meta: v.displayName,
        }))
        .filter((v) => completionPrefix && v.value.startsWith(completionPrefix))
        .map((v) => ({
          value: v.value.split('.')[dotCount],
          meta: v.value.split('.').length - 1 === dotCount ? v.meta : '',
        }))
        .filter((v) => v.value !== completionPrefix);

      callback(
        null,
        fixedVariablesSuggestions.map((item, index) => ({ ...item, caption: item.value, score: 500 - index }))
      );
    },
  };
};
